/**
 * A aresta do canvas, com nome do ramo e botão de excluir.
 *
 * Três problemas de leitura do fluxo se resolvem aqui:
 *
 * 1. **Não havia como excluir uma conexão.** O React Flow já desenha uma faixa invisível de clique
 *    em volta da linha (`interactionWidth`), mas nada indicava isso. Agora, ao passar o mouse, surge
 *    uma pílula no meio da linha com o ✕. Ela fica fixa quando a aresta está selecionada, e o
 *    Delete/Backspace continua valendo.
 * 2. **Não se sabia de qual saída a linha partia.** A pílula mostra o nome do ramo — o texto do
 *    botão, `verdadeiro`/`falso`, `erro` — e o traço usa a cor da porta de origem.
 * 3. **A direção não se lia.** Traçado ortogonal e seta na ponta.
 *
 * A exclusão passa pelo `removeEdge` do store, e não pelo `deleteElements` da biblioteca, para toda
 * mutação do rascunho continuar entrando por um único caminho (que é quem marca `dirty` e agenda o
 * autosave).
 */

import { useState } from 'react';
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath, type EdgeProps } from '@xyflow/react';
import { TONE_COLORS, type BranchEdgeData, type HandleTone } from './mapping.js';
import { useEditor } from '../store/editor.js';

export function BranchEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
  markerEnd,
  style,
}: EdgeProps) {
  const [hovered, setHovered] = useState(false);
  const removeEdge = useEditor((state) => state.removeEdge);

  const [path, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    borderRadius: 10,
  });

  const branch = data as BranchEdgeData | undefined;
  const tone: HandleTone = branch?.tone ?? 'default';
  const color = TONE_COLORS[tone];
  const visible = hovered || Boolean(selected);

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        markerEnd={markerEnd}
        style={{ ...style, ...(selected ? { strokeWidth: 3 } : {}) }}
        // Faixa de clique generosa: linha de 2px é alvo cruel de mirar com o mouse.
        interactionWidth={26}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      />

      <EdgeLabelRenderer>
        <div
          // `pointer-events: all` é necessário: o container das etiquetas é inerte por padrão.
          className="nodrag nopan pointer-events-auto absolute flex items-center gap-1 rounded-full border bg-white py-1 pr-1 pl-2 shadow-sm transition-opacity"
          style={{
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            borderColor: color,
            opacity: visible ? 1 : 0,
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {branch?.label ? (
            <span className="max-w-28 truncate text-xs font-medium" style={{ color }}>
              {branch.label}
            </span>
          ) : null}
          <button
            type="button"
            title="Excluir esta conexão"
            aria-label={`Excluir a conexão${branch?.label ? ` do ramo ${branch.label}` : ''}`}
            onClick={(event) => {
              event.stopPropagation();
              removeEdge(id);
            }}
            className="grid h-5 w-5 place-items-center rounded-full text-xs leading-none text-slate-500 transition hover:bg-red-50 hover:text-red-600"
          >
            ×
          </button>
        </div>
      </EdgeLabelRenderer>
    </>
  );
}
